import {
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getSortedRowModel,
	useReactTable,
	type ColumnFiltersState,
	type SortingState,
} from '@tanstack/react-table';
import {
	Activity,
	ArrowUpDown,
	Calendar,
	ChevronDown,
	ChevronUp,
	Copy,
	Edit,
	FileText,
	Filter,
	Globe,
	Hash,
	Trash2,
	Upload,
	Webhook,
} from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { useAuth } from '../../contexts/AuthContext';
import { useCloneFlow, useDeleteFlow, usePublishFlow } from '../../hooks/useFlows';
import { usePagination } from '../../hooks/usePagination';
import { flowsApi } from '../../lib/api-client';
import type { Flow } from '../../types/api';
import type { FlowPaginationParams } from '../../types/pagination';
import { ApiTestingModal } from '../ApiTestingModal';
import { useDialog } from '../DialogProvider';
import { ExecutionLaunchModal } from '../ExecutionLaunchModal';
import { NestedFolderSelect } from '../folders/NestedFolderSelect';
import { PaginationControls } from '../shared/PaginationControls';
import { SearchInput } from '../shared/SearchInput';
import { TableSkeleton } from '../shared/TableSkeleton';
import { WebhookTestModal } from '../WebhookTestModal';

/**
 * Props for the FlowsTable component
 */
interface FlowsTableProps {
	initialFilters?: Partial<FlowPaginationParams>;
	showSearch?: boolean;
	showFilters?: boolean;
	className?: string;
}

/**
 * Column helper for type-safe column definitions
 */
const columnHelper = createColumnHelper<Flow>();

/**
 * Helper function to check if a workflow has a start.form trigger node
 */
const hasStartFormTrigger = (flow: Flow): boolean => {
	if (!flow.dagDefinition?.nodes) return false;
	return flow.dagDefinition.nodes.some((node: any) => node.type === 'start.form');
};

/**
 * Helper function to check if a workflow has any form trigger (legacy or new)
 */
const hasFormTrigger = (flow: Flow): boolean => {
	return !!flow.submissionForm || hasStartFormTrigger(flow);
};

/**
 * Helper function to check if a workflow has a start.webhook trigger node
 */
const hasWebhookTrigger = (flow: Flow): boolean => {
	if (!flow.dagDefinition?.nodes) return false;
	return flow.dagDefinition.nodes.some((node: any) => node.type === 'start.webhook');
};

/**
 * Helper function to check if a workflow has a start.http-endpoint trigger node
 */
const hasHttpEndpointTrigger = (flow: Flow): boolean => {
	if (!flow.dagDefinition?.nodes) return false;
	return flow.dagDefinition.nodes.some((node: any) => node.type === 'start.http-endpoint');
};

/**
 * Helper function to get webhook configuration from a workflow
 */
const getWebhookConfig = (flow: Flow) => {
	if (!flow.dagDefinition?.nodes) return null;
	const webhookNode = flow.dagDefinition.nodes.find((node: any) => node.type === 'start.webhook');
	return webhookNode?.data?.config;
};

/**
 * Performant flows table with server-side pagination, sorting, filtering, and search
 */
export function FlowsTable({
	initialFilters = {},
	showSearch = true,
	showFilters = true,
	className = '',
}: FlowsTableProps) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
	const [selectedFlowForExecution, setSelectedFlowForExecution] = useState<Flow | null>(null);
	const [selectedFlowForWebhookTest, setSelectedFlowForWebhookTest] = useState<Flow | null>(null);
	const [selectedFlowForApiTest, setSelectedFlowForApiTest] = useState<Flow | null>(null);

	// Memoize the initial params to prevent re-initialization
	const memoizedInitialParams = useMemo(
		() => ({
			limit: 20,
			sortBy: 'updatedAt' as const,
			sortOrder: 'DESC' as const,
			...initialFilters,
		}),
		[JSON.stringify(initialFilters)]
	);

	// Use pagination hook for server-side pagination
	const {
		data: flowsData,
		pagination,
		loading,
		error,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		search,
		updateParams,
		changeLimit,
		clearFilters,
		currentParams,
		refresh,
	} = usePagination<Flow, FlowPaginationParams>(flowsApi.getFlows, memoizedInitialParams);

	// Ensure flows is always an array and track initial load
	const flows = flowsData || [];
	const hasInitialData = useRef(false);

	// Track if we've received initial data to prevent flash
	useEffect(() => {
		if (flows.length > 0 && !hasInitialData.current) {
			hasInitialData.current = true;
		}
	}, [flows.length]);

	// Mutations
	const cloneFlowMutation = useCloneFlow();
	const deleteFlowMutation = useDeleteFlow();
	const { confirm, alert } = useDialog();
	const { canWrite, canExecute, canDelete, hasAnyRole } = useAuth();
	const publishFlowMutation = usePublishFlow();

	// Add success handlers to refresh data after mutations
	const handleCloneSuccess = () => {
		// Refresh the data after successful clone
		refresh();
	};

	const handleDeleteSuccess = () => {
		// Refresh the data after successful delete
		refresh();
	};

	// Handle delete workflow with confirmation
	const handleDeleteWorkflow = (workflow: Flow) => {
		confirm({
			title: 'Delete Workflow',
			message: `Are you sure you want to delete "${workflow.name}"?\n\nThis action cannot be undone. The workflow and all its execution history will be permanently removed.`,
			confirmText: 'Delete',
			cancelText: 'Cancel',
			type: 'danger',
			onConfirm: () => {
				deleteFlowMutation.mutate(workflow.id, {
					onSuccess: handleDeleteSuccess,
				});
			},
		});
	};

	const handlePublishSuccess = () => {
		// Refresh the data after successful publish
		refresh();
	};

	const handlePublishError = (error: any) => {
		// Show error message to user
		console.error('Failed to publish workflow:', error);

		// Extract error message from API response
		let errorMessage = 'Failed to publish workflow';
		if (error?.response?.data?.message) {
			errorMessage = error.response.data.message;
		} else if (error?.message) {
			errorMessage = error.message;
		}

		// Show styled error dialog
		alert({
			title: 'Publish Failed',
			message: errorMessage,
			type: 'error',
		});
	};

	const handleExecutionStarted = (executionId: string) => {
		// Refresh the data after successful execution
		refresh();
		console.log('Execution started:', executionId);
	};

	// Handle server-side sorting
	const handleSortingChange = (
		updaterOrValue: SortingState | ((old: SortingState) => SortingState)
	) => {
		const newSorting =
			typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
		setSorting(newSorting);
		if (newSorting.length > 0) {
			const sort = newSorting[0];
			updateParams({
				sortBy: sort.id as FlowPaginationParams['sortBy'],
				sortOrder: sort.desc ? 'DESC' : 'ASC',
			});
		} else {
			updateParams({
				sortBy: 'updatedAt',
				sortOrder: 'DESC',
			});
		}
	};

	// Filter handlers
	const handleStatusFilter = (status: string) => {
		updateParams({
			status: status === 'all' ? undefined : (status as FlowPaginationParams['status']),
		});
	};

	const handleFolderFilter = (folderId: string) => {
		updateParams({
			folderId: folderId === 'all' ? undefined : folderId,
		});
	};

	// Table columns definition
	const columns = useMemo(
		() => [
			columnHelper.accessor('name', {
				header: ({ column }) => (
					<button
						className="flex gap-2 items-center font-medium text-left"
						onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					>
						Name
						<ArrowUpDown className="w-4 h-4" />
					</button>
				),
				cell: ({ row }) => (
					<div className="flex gap-3 items-center">
						<div className="flex-shrink-0">
							<FileText className="w-5 h-5 text-blue-500" />
						</div>
						<div className="flex flex-col">
							<Link
								to={`/workflows/${row.original.id}/edit`}
								className="font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
							>
								{row.original.name}
							</Link>
							{row.original.description && (
								<span className="mt-1 text-sm text-gray-500 dark:text-gray-400">
									{row.original.description.length > 50
										? `${row.original.description.substring(0, 50)}...`
										: row.original.description}
								</span>
							)}
						</div>
					</div>
				),
			}),
			columnHelper.accessor('folder.name', {
				header: 'Folder',
				cell: ({ getValue }) => {
					const folderName = getValue();
					return folderName ? (
						<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
							{folderName}
						</span>
					) : (
						<span className="text-sm text-gray-400">No folder</span>
					);
				},
			}),
			columnHelper.accessor('status', {
				header: ({ column }) => (
					<button
						className="flex gap-2 items-center font-medium text-left"
						onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					>
						Status
						<ArrowUpDown className="w-4 h-4" />
					</button>
				),
				cell: ({ getValue }) => {
					const status = getValue();
					const statusColors = {
						draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
						published:
							'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
						archived: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
					};
					return (
						<span
							className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
								statusColors[status] || statusColors.draft
							}`}
						>
							{status.toUpperCase()}
						</span>
					);
				},
			}),
			// TODO: Backend needs to populate flow.statistics.totalExecutions field
			// Currently this field is not being updated when executions complete
			// Uncomment when backend implements updateFlowStatistics in execution flow
			/*
    columnHelper.accessor('statistics.totalExecutions', {
      header: 'Executions',
      cell: ({ getValue, row }) => {
        // Handle cases where statistics might be undefined or null
        const count = getValue() || row.original.statistics?.totalExecutions || 0;
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
            {count} execution{count !== 1 ? 's' : ''}
          </span>
        );
      },
    }),
    */
			columnHelper.accessor('tags', {
				header: 'Tags',
				cell: ({ getValue }) => {
					const tags = (getValue() as any[]) || [];
					if (tags.length === 0)
						return (
							<span className="text-sm text-gray-400 dark:text-gray-500">
								No tags
							</span>
						);

					// Handle both Tag objects and string arrays for backward compatibility
					const displayTags = tags.slice(0, 3).map(tag => ({
						name: typeof tag === 'string' ? tag : tag.name,
						colorHue:
							typeof tag === 'string'
								? Array.from(tag).reduce(
										(acc, char) => (acc * 31 + char.charCodeAt(0)) % 360,
										0
								  )
								: tag.colorHue || 0,
						usageCount: typeof tag === 'string' ? 0 : tag.usageCount || 0,
					}));

					return (
						<div className="flex flex-wrap gap-1 max-w-xs">
							{displayTags.map((tag, index) => {
								const colorHue = tag.colorHue;
								const lightColor = `hsl(${colorHue}, 45%, 90%)`;
								const textColor = `hsl(${colorHue}, 70%, 35%)`;
								const darkBgColor = `hsl(${colorHue}, 30%, 20%)`;
								const darkTextColor = `hsl(${colorHue}, 60%, 80%)`;

								return (
									<span
										key={`${tag.name}-${index}`}
										className="inline-flex gap-1 items-center px-2 py-1 text-xs rounded transition-colors dark:bg-opacity-80"
										style={{
											backgroundColor: lightColor,
											color: textColor,
										}}
										title={
											tag.usageCount > 0
												? `Used in ${tag.usageCount} workflows`
												: tag.name
										}
									>
										<Hash className="w-2.5 h-2.5" />
										<span className="font-medium">{tag.name}</span>
									</span>
								);
							})}
							{tags.length > 3 && (
								<span className="self-center text-xs text-gray-500 dark:text-gray-400">
									+{tags.length - 3} more
								</span>
							)}
						</div>
					);
				},
			}),
			columnHelper.accessor('createdAt', {
				header: ({ column }) => (
					<button
						className="flex gap-2 items-center font-medium text-left"
						onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					>
						<Calendar className="w-4 h-4" />
						Created
						<ArrowUpDown className="w-4 h-4" />
					</button>
				),
				cell: ({ getValue }) => {
					const date = new Date(getValue());
					return (
						<div className="text-sm">
							<div>{date.toLocaleDateString()}</div>
							<div className="text-gray-500 dark:text-gray-400">
								{date.toLocaleTimeString()}
							</div>
						</div>
					);
				},
			}),
			columnHelper.accessor('updatedAt', {
				header: ({ column }) => (
					<button
						className="flex gap-2 items-center font-medium text-left"
						onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
					>
						<Calendar className="w-4 h-4" />
						Updated
						<ArrowUpDown className="w-4 h-4" />
					</button>
				),
				cell: ({ getValue }) => {
					const date = new Date(getValue());
					return (
						<div className="text-sm">
							<div>{date.toLocaleDateString()}</div>
							<div className="text-gray-500 dark:text-gray-400">
								{date.toLocaleTimeString()}
							</div>
						</div>
					);
				},
			}),
			columnHelper.display({
				id: 'actions',
				header: 'Actions',
				cell: ({ row }) => (
					<div className="flex gap-2 items-center">
						{/* Edit - Only WRITE+ users */}
						{canWrite() && (
							<Link
								to={`/workflows/${row.original.id}/edit`}
								className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
								data-tooltip-id={`edit-${row.original.id}`}
								data-tooltip-content="Edit workflow"
							>
								<Edit className="w-4 h-4" />
							</Link>
						)}
						{/* Take Form - READ+ users (interactive forms require READ+) */}
						{hasFormTrigger(row.original) &&
							hasAnyRole(['READ', 'EXECUTE', 'WRITE', 'SUPPORT', 'ADMIN']) && (
								<Link
									to={`/flows/${row.original.id}/form`}
									className="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
									data-tooltip-id={`form-${row.original.id}`}
									data-tooltip-content={
										row.original.status === 'published'
											? 'Take form (published)'
											: 'Take form (draft - test mode)'
									}
								>
									<FileText className="w-4 h-4" />
								</Link>
							)}
						{/* Test Webhook - EXECUTE+ users */}
						{hasWebhookTrigger(row.original) && canExecute() && (
							<button
								onClick={() => setSelectedFlowForWebhookTest(row.original)}
								className="p-1 text-gray-500 hover:text-orange-600 dark:text-gray-400 dark:hover:text-orange-400"
								data-tooltip-id={`webhook-test-${row.original.id}`}
								data-tooltip-content={
									row.original.status === 'published'
										? 'Test webhook (published)'
										: 'Test webhook (draft - test mode)'
								}
							>
								<Webhook className="w-4 h-4" />
							</button>
						)}
						{/* Test API Endpoint - EXECUTE+ users */}
						{hasHttpEndpointTrigger(row.original) && canExecute() && (
							<>
								<button
									onClick={() => setSelectedFlowForApiTest(row.original)}
									className="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
									data-tooltip-id={`api-test-${row.original.id}`}
									data-tooltip-content={
										row.original.status === 'published'
											? 'Test API endpoint (published)'
											: 'Test API endpoint (draft - test mode)'
									}
								>
									<Globe className="w-4 h-4" />
								</button>
								<Tooltip id={`api-test-${row.original.id}`} />
							</>
						)}
						{/* Execute Workflow - EXECUTE+ users */}
						{/* Disabled: Launch execution removed from list view */}
						{/* {canExecute() && (
						<button
							onClick={() => setSelectedFlowForExecution(row.original)}
							className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
							data-tooltip-id={`execute-${row.original.id}`}
							data-tooltip-content="Launch execution"
						>
							<Play className="w-4 h-4" />
						</button>
					)} */}
						{/* View Executions - READ+ users (execution interaction requires READ+) */}
						{hasAnyRole(['READ', 'EXECUTE', 'WRITE', 'SUPPORT', 'ADMIN']) && (
							<Link
								to={`/executions?flowId=${row.original.id}`}
								className="p-1 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"
								data-tooltip-id={`executions-${row.original.id}`}
								data-tooltip-content="View all executions"
							>
								<Activity className="w-4 h-4" />
							</Link>
						)}
						{/* Clone Workflow - WRITE+ users */}
						{canWrite() && (
							<button
								onClick={() =>
									cloneFlowMutation.mutate(
										{
											id: row.original.id,
											name: `${row.original.name} (Copy)`,
										},
										{ onSuccess: handleCloneSuccess }
									)
								}
								className="p-1 text-gray-500 hover:text-green-600 dark:text-gray-400 dark:hover:text-green-400"
								disabled={cloneFlowMutation.isPending}
								data-tooltip-id={`clone-${row.original.id}`}
								data-tooltip-content="Clone workflow"
							>
								<Copy className="w-4 h-4" />
							</button>
						)}
						{/* Publish Workflow - WRITE+ users */}
						{row.original.status === 'draft' && canWrite() && (
							<button
								onClick={() =>
									publishFlowMutation.mutate(row.original.id, {
										onSuccess: handlePublishSuccess,
										onError: handlePublishError,
									})
								}
								className="p-1 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
								disabled={publishFlowMutation.isPending}
								data-tooltip-id={`publish-${row.original.id}`}
								data-tooltip-content="Publish workflow"
							>
								<Upload className="w-4 h-4" />
							</button>
						)}
						{/* Delete Workflow - ADMIN users only */}
						{canDelete() && (
							<button
								onClick={() => handleDeleteWorkflow(row.original)}
								className="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
								disabled={deleteFlowMutation.isPending}
								data-tooltip-id={`delete-${row.original.id}`}
								data-tooltip-content="Delete workflow"
							>
								<Trash2 className="w-4 h-4" />
							</button>
						)}

						{/* Tooltips */}
						<Tooltip id={`edit-${row.original.id}`} />
						<Tooltip id={`form-${row.original.id}`} />
						<Tooltip id={`execute-${row.original.id}`} />
						<Tooltip id={`executions-${row.original.id}`} />
						<Tooltip id={`clone-${row.original.id}`} />
						<Tooltip id={`publish-${row.original.id}`} />
						<Tooltip id={`delete-${row.original.id}`} />
					</div>
				),
			}),
		],
		[
			cloneFlowMutation,
			deleteFlowMutation,
			publishFlowMutation,
			refresh,
			handlePublishError,
			handleDeleteWorkflow,
			confirm,
			canWrite,
			canExecute,
			hasAnyRole,
		]
	);
	// Create table instance (client-side features only, pagination handled by server)
	const table = useReactTable({
		data: flows,
		columns,
		state: {
			sorting,
			columnFilters,
		},
		onSortingChange: handleSortingChange,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		manualPagination: true, // Server-side pagination
		manualSorting: true, // Server-side sorting
	});

	if (error) {
		return (
			<div className="flex flex-col justify-center items-center py-12">
				<div className="mb-4 text-red-600 dark:text-red-400">
					Error loading workflows: {error}
				</div>
				<button
					onClick={() => window.location.reload()}
					className="px-4 py-2 text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
				>
					Retry
				</button>
			</div>
		);
	}

	return (
		<div className={`relative space-y-6 w-full ${className}`}>
			{/* Search and Filters - memoized to prevent re-renders */}
			{useMemo(
				() =>
					(showSearch || showFilters) && (
						<div className="flex flex-col gap-4 justify-between items-start p-4 bg-gray-50 rounded-lg border border-gray-200 sm:flex-row sm:items-center dark:bg-gray-800 dark:border-gray-700">
							{showSearch && (
								<SearchInput
									onSearch={search}
									placeholder="Search workflows..."
									className="w-full sm:w-auto sm:min-w-[300px]"
								/>
							)}

							{showFilters && (
								<div className="flex gap-2 items-center">
									<button
										onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
										className="flex gap-2 items-center px-3 py-2 text-sm rounded-md border border-gray-300 transition-colors dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
									>
										<Filter className="w-4 h-4" />
										Filters
										{showAdvancedFilters ? (
											<ChevronUp className="w-4 h-4" />
										) : (
											<ChevronDown className="w-4 h-4" />
										)}
									</button>
								</div>
							)}
						</div>
					),
				[showSearch, showFilters, search, showAdvancedFilters]
			)}

			{/* Advanced Filters Panel */}
			{showAdvancedFilters && (
				<div className="p-4 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Folder
							</label>
							<NestedFolderSelect
								value={currentParams.folderId || ''}
								onChange={folderId => handleFolderFilter(folderId || 'all')}
								placeholder="All folders"
								isClearable={true}
								className="text-sm"
							/>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Status
							</label>
							<select
								value={currentParams.status || 'all'}
								onChange={e => handleStatusFilter(e.target.value)}
								className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
							>
								<option value="all">All Statuses</option>
								<option value="draft">Draft</option>
								<option value="published">Published</option>
								<option value="archived">Archived</option>
							</select>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Sort By
							</label>
							<select
								value={currentParams.sortBy || 'updatedAt'}
								onChange={e =>
									updateParams({
										sortBy: e.target.value as FlowPaginationParams['sortBy'],
									})
								}
								className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
							>
								<option value="name">Name</option>
								<option value="createdAt">Created Date</option>
								<option value="updatedAt">Updated Date</option>
								<option value="status">Status</option>
							</select>
						</div>

						<div>
							<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
								Order
							</label>
							<select
								value={currentParams.sortOrder || 'DESC'}
								onChange={e =>
									updateParams({ sortOrder: e.target.value as 'ASC' | 'DESC' })
								}
								className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
							>
								<option value="ASC">Ascending</option>
								<option value="DESC">Descending</option>
							</select>
						</div>
					</div>
				</div>
			)}

			{/* Loading State - show skeleton loader to prevent flash */}
			{loading && flows.length === 0 && !hasInitialData.current && (
				<TableSkeleton rows={5} columns={6} />
			)}

			{/* Table */}
			<div className="overflow-x-auto relative w-full rounded-lg border border-gray-200 dark:border-gray-700">
				{/* Subtle loading overlay */}
				{loading && hasInitialData.current && (
					<div className="flex absolute inset-0 z-10 justify-center items-center backdrop-blur-sm bg-white/50 dark:bg-gray-900/50">
						<div className="flex gap-2 items-center px-4 py-2 bg-white rounded-lg border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
							<div className="w-4 h-4 rounded-full border-2 border-blue-600 animate-spin border-t-transparent"></div>
							<span className="text-sm text-gray-600 dark:text-gray-400">
								Updating...
							</span>
						</div>
					</div>
				)}
				<table className="w-full min-w-full divide-y divide-gray-200 transition-opacity duration-200 dark:divide-gray-700">
					<thead className="bg-gray-50 dark:bg-gray-800">
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => (
									<th
										key={header.id}
										className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-400"
									>
										{header.isPlaceholder
											? null
											: flexRender(
													header.column.columnDef.header,
													header.getContext()
											  )}
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-700">
						{table.getRowModel().rows.map(row => (
							<tr
								key={row.id}
								className="transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
							>
								{row.getVisibleCells().map(cell => (
									<td key={cell.id} className="px-6 py-4 whitespace-nowrap">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{/* No Results - show with transition to prevent flash */}
			{!loading && flows.length === 0 && hasInitialData.current && (
				<div className="py-12 text-center opacity-100 transition-opacity duration-200">
					<div className="mb-4 text-gray-500 dark:text-gray-400">
						No workflows found matching your criteria
					</div>
					<button
						onClick={clearFilters}
						className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
					>
						Clear all filters
					</button>
				</div>
			)}

			{/* Pagination Controls */}
			{pagination && (
				<PaginationControls
					pagination={pagination}
					onPageChange={goToPage}
					onNextPage={goToNextPage}
					onPreviousPage={goToPreviousPage}
					loading={loading}
					pageSizeOptions={[10, 20, 50]}
					onPageSizeChange={changeLimit}
					showPageSizeSelector={true}
				/>
			)}

			{/* Execution Launch Modal */}
			{selectedFlowForExecution && (
				<ExecutionLaunchModal
					flow={selectedFlowForExecution}
					isOpen={!!selectedFlowForExecution}
					onClose={() => setSelectedFlowForExecution(null)}
					onExecutionStarted={handleExecutionStarted}
				/>
			)}

			{/* Webhook Test Modal */}
			{selectedFlowForWebhookTest && (
				<WebhookTestModal
					flow={selectedFlowForWebhookTest}
					isOpen={!!selectedFlowForWebhookTest}
					onClose={() => setSelectedFlowForWebhookTest(null)}
				/>
			)}

			{/* API Testing Modal */}
			{selectedFlowForApiTest && (
				<ApiTestingModal
					workflow={selectedFlowForApiTest}
					isOpen={!!selectedFlowForApiTest}
					onClose={() => setSelectedFlowForApiTest(null)}
				/>
			)}
		</div>
	);
}

// Memoize the component to prevent unnecessary re-renders
export default memo(FlowsTable, (prevProps, nextProps) => {
	// Custom comparison function to prevent unnecessary re-renders
	return (
		JSON.stringify(prevProps.initialFilters) === JSON.stringify(nextProps.initialFilters) &&
		prevProps.showSearch === nextProps.showSearch &&
		prevProps.showFilters === nextProps.showFilters &&
		prevProps.className === nextProps.className
	);
});
